// ============================================================================
// checklistWriter — one uniform write API for every direct write site.
// ----------------------------------------------------------------------------
// Before this file existed the codebase had ~40 direct
// `updateDoc(doc(firestoreDb, 'checklist_items', id), …)` sites across
// BoardExplorer, ForYouPage, NodeDetailDrawer, RichChecklistEditor, hooks,
// etc. That produced two problems:
//
//   1. Every site had to be updated whenever the storage model changed.
//   2. The book model (definition + book page + layout version) had to catch
//      up via the mirror trigger — a real write→snapshot→mirror round-trip.
//
// This module centralizes the write intent. Every direct write site now calls
// one of these functions; the function knows how to route the payload into
// the right sub-collections of the new model, and (only during the compat
// window) also updates the legacy `checklist_items` doc so anything still
// reading from there stays consistent.
//
//   writeChecklistUpdate(id, updates, actor)   — patch existing checklist
//   createChecklistNative({...}, actor)        — new checklist end-to-end
//   deleteChecklistNative(id, actor)           — remove definition + page entry
//
// See src/types/checklistBooks.ts for the underlying model. Once every site
// stops reading from `checklist_items`, drop the legacy branch in each
// function below.
// ============================================================================

import {
  arrayUnion,
  collection,
  deleteField,
  doc,
  getDoc,
  increment,
  serverTimestamp,
  setDoc,
  writeBatch,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { runChecklistCreateTransaction, isSyntheticParentId } from './checklistCounterTransaction'
import { binChecklist, deleteChecklistForever } from './elementRestructureService'
import type { TimelineBoardKey } from '../types/schema'
import { findEmptyCanvasSlot, toSlotBox } from './canvasPlacementService'
import { NODE_HEIGHT, NODE_WIDTH } from './canvasLayoutConstants'
import {
  bookIdFor,
  checklistPath,
  type ChecklistCompletionState,
  type ChecklistDefinition,
  type ChecklistType,
} from '../types/checklistBooks'
import {
  canParentHostRecurring,
  isLegacyRecurringContainerId,
} from '../utils/recurringParentEligibility'
import {
  ensurePageForKey,
  ensureRecurringBookWindow,
  listPages,
  pageKeyFor,
  removeChecklistFromPage,
  setChecklistCompletionOnPage,
} from './checklistBookService'
import {
  applyEditToPage,
  getLayoutVersion,
} from './layoutVersionService'
import { invalidateChecklistDefinitionListCache } from './checklistReadCache'

// ---------------------------------------------------------------------------
// Error classification — used to keep the compat-window `not-found` no-ops
// while letting real errors (permission-denied, network, quota, ...) bubble
// up to the caller so the drawer / member UI can surface a toast instead of
// pretending the save succeeded.
// ---------------------------------------------------------------------------

const isNotFoundError = (err: unknown): boolean =>
  (err as { code?: string })?.code === 'not-found'

const swallowNotFound = (context: string) => (err: unknown): void => {
  if (isNotFoundError(err)) {
    console.debug(`[${context}] doc missing, expected post-wipe`, (err as Error)?.message)
    return
  }
  console.warn(`[${context}] failed`, (err as Error)?.message ?? err)
  throw err
}

// ---------------------------------------------------------------------------
// Actor
// ---------------------------------------------------------------------------

export type ChecklistWriterActor = { uid: string; displayName?: string | null }

const bookActor = (actor: ChecklistWriterActor) => ({
  uid: actor.uid,
  displayName: actor.displayName ?? undefined,
})

// ---------------------------------------------------------------------------
// Field categorization — mirrors the split used by checklistMutationService
// and the checklistBookMirror trigger.
// ---------------------------------------------------------------------------

const COMPLETION_FIELD_KEYS = new Set<keyof ChecklistCompletionState>([
  'isCompleted', 'boardStatus', 'workflowStatus', 'previousWorkflowStatus',
  'completedAt', 'completedById', 'completedByName',
  'isReady', 'resourceReadySku', 'resourceReadyAt',
  'proofFileUrl', 'proofFileName', 'proofFileStoragePath',
  'proofSubmissionId', 'proofUploadedById', 'proofUploadedByName', 'proofUploadedAt',
  'coSignedByUid', 'coSignedByName', 'isPendingReview',
  'proofReviewStatus', 'proofReviewedAt', 'proofReviewedById', 'proofReviewedByName', 'proofRejectionNote',
  'verifiedCoordinates', 'proofVideoUrl',
  'memoStoragePath', 'memoFileName', 'memoContentType',
  'memoUploadedById', 'memoUploadedByName', 'memoUploadedAt', 'voiceMemoBlobUrl',
  'timerStartedAt', 'timerExpiredAt', 'isUrgentCrisisMode',
  'commentsCount', 'commentsCountDelta', 'actualCost',
  'statusSyncOrigin', 'statusSyncVersion',
] as unknown as Array<keyof ChecklistCompletionState>)

const extractIncrementDelta = (raw: unknown): 1 | -1 | null => {
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as Record<string, unknown>
  const method = obj._methodName
  const isIncrement =
    typeof method === 'string' && (method === 'increment' || method.endsWith('.increment'))
  const operand =
    typeof obj._operand === 'number'
      ? obj._operand
      : typeof obj.operand === 'number'
        ? obj.operand
        : null
  if (isIncrement && (operand === 1 || operand === -1)) {
    return operand
  }
  return null
}

const AUDIT_AND_LEGACY_KEYS = new Set([
  'updatedAt', 'lastUpdatedBy', 'lastUpdatedByName', 'recordVersion',
  // Layout / graph fields that belong on layoutVersion, not the definition:
  'workspaceCanvasLayoutVersion',
  // Legacy singleton artefacts:
  'occurrences', 'recurrenceTemplateId', 'recurrenceBlueprintId',
  'occurrenceKey', 'isRecurrenceInstance',
])

const splitUpdates = (
  updates: Record<string, unknown>,
): {
  definition: Record<string, unknown>
  completion: Partial<ChecklistCompletionState> & Record<string, unknown>
  position: { x: number; y: number } | null
} => {
  const definition: Record<string, unknown> = {}
  const completion: Partial<ChecklistCompletionState> & Record<string, unknown> = {}
  let position: { x: number; y: number } | null = null
  for (const [key, value] of Object.entries(updates)) {
    if (key === 'position' && value && typeof value === 'object') {
      const p = value as { x?: number; y?: number }
      if (typeof p.x === 'number' && typeof p.y === 'number') {
        position = { x: p.x, y: p.y }
      }
      continue
    }
    if (COMPLETION_FIELD_KEYS.has(key as keyof ChecklistCompletionState)) {
      completion[key] = value
      continue
    }
    if (AUDIT_AND_LEGACY_KEYS.has(key)) continue
    definition[key] = value
  }
  return { definition, completion, position }
}

/** Locate a checklist by id via the new-model definition doc. Returns null
 *  if the definition doesn't exist. */
const readChecklist = async (
  id: string,
): Promise<{ workspaceId: string; checklistType: ChecklistType; board?: TimelineBoardKey } | null> => {
  const defSnap = await getDoc(doc(firestoreDb, checklistPath(id)))
  if (!defSnap.exists()) return null
  const d = defSnap.data() as { workspaceId?: string; checklistType?: ChecklistType; board?: TimelineBoardKey }
  if (!d.workspaceId) return null
  return {
    workspaceId: d.workspaceId,
    checklistType: (d.checklistType as ChecklistType) ?? 'INDIVIDUAL',
    board: d.board,
  }
}

// ---------------------------------------------------------------------------
// Public API — update
// ---------------------------------------------------------------------------

/**
 * Uniform patch for a checklist. Splits `updates` into three targets:
 *   - definition-scoped fields → `checklists/{id}` (merge)
 *   - completion-scoped fields → book page `completions[id]`
 *   - position → new layout version via applyEditToPage
 *
 * Also updates the legacy `checklist_items/{id}` doc so any code still
 * reading from there stays coherent during the compat window. When the
 * codebase no longer reads legacy, drop the `legacyUpdate` block below.
 */
export const writeChecklistUpdate = async (
  id: string,
  updates: Record<string, unknown>,
  actor: ChecklistWriterActor,
  opts: { pageKey?: string | null } = {},
): Promise<void> => {
  const { definition, completion, position } = splitUpdates(updates)
  const nowActorName = actor.displayName ?? null
  const auditSuffix = {
    updatedAt: serverTimestamp(),
    lastUpdatedBy: actor.uid || null,
    lastUpdatedByName: nowActorName,
  }

  const meta = await readChecklist(id)
  if (!meta) return

  const promises: Promise<unknown>[] = []

  // 3. Definition merge — authoritative; propagate real errors so the caller
  // sees a failed Save rather than a silent lie.
  if (Object.keys(definition).length > 0) {
    promises.push(
      setDoc(
        doc(firestoreDb, checklistPath(id)),
        { ...definition, ...auditSuffix },
        { merge: true },
      )
        .then(() => invalidateChecklistDefinitionListCache(meta.workspaceId, meta.checklistType)),
    )
  }

  // 4. Page completion patch (today's page).
  if (Object.keys(completion).length > 0) {
    const isDailyOrWeekly = meta.checklistType === 'DAILY' || meta.checklistType === 'WEEKLY'
    if (isDailyOrWeekly) {
      const { isRepeatPageStateKey } = await import('./repeat/completions')
      const hasState = Object.keys(completion).some((k) => isRepeatPageStateKey(k))
      if (hasState) {
        throw new Error('Daily/Weekly status changes go through updateChecklistWorkflowStatus')
      }
    }
    let pageKey = opts.pageKey
    if (!pageKey && isDailyOrWeekly) {
      const { fetchWorkspaceRawTimezone } = await import('./checklistStatusWriter')
      const { workspaceTodayKey } = await import('./repeat/keys')
      const rawTz = await fetchWorkspaceRawTimezone(meta.workspaceId)
      pageKey = workspaceTodayKey(meta.checklistType as 'DAILY' | 'WEEKLY', rawTz)
    } else if (!pageKey) {
      const { fetchWorkspaceRawTimezone } = await import('./checklistStatusWriter')
      const rawTz = await fetchWorkspaceRawTimezone(meta.workspaceId)
      pageKey = pageKeyFor(meta.checklistType, new Date(), rawTz)
    }
    const bookId = bookIdFor(meta.workspaceId, meta.checklistType)
    const patchPayload: Record<string, unknown> = { ...completion }
    if (isDailyOrWeekly && 'commentsCount' in patchPayload) {
      const delta = extractIncrementDelta(patchPayload.commentsCount)
      if (delta === null) {
        throw new Error('Daily/Weekly commentsCount update must be increment(1) or increment(-1) (or use commentsCountDelta)')
      }
      delete patchPayload.commentsCount
      patchPayload.commentsCountDelta = delta
    }
    if (!isDailyOrWeekly && 'commentsCountDelta' in patchPayload) {
      const rawDelta = patchPayload.commentsCountDelta
      if (rawDelta !== 1 && rawDelta !== -1) {
        throw new Error('commentsCountDelta must be 1 or -1')
      }
      delete patchPayload.commentsCountDelta
      patchPayload.commentsCount = increment(rawDelta)
    }
    promises.push(
      setChecklistCompletionOnPage(bookId, pageKey, id, patchPayload, bookActor(actor)),
    )
  }

  // 5. Native layout write when the caller changed position.
  if (position) {
    const isDailyOrWeekly = meta.checklistType === 'DAILY' || meta.checklistType === 'WEEKLY'
    let pageKey = opts.pageKey
    if (!pageKey && isDailyOrWeekly) {
      const { fetchWorkspaceRawTimezone } = await import('./checklistStatusWriter')
      const { workspaceTodayKey } = await import('./repeat/keys')
      const rawTz = await fetchWorkspaceRawTimezone(meta.workspaceId)
      pageKey = workspaceTodayKey(meta.checklistType as 'DAILY' | 'WEEKLY', rawTz)
    } else if (!pageKey) {
      const { fetchWorkspaceRawTimezone } = await import('./checklistStatusWriter')
      const rawTz = await fetchWorkspaceRawTimezone(meta.workspaceId)
      pageKey = pageKeyFor(meta.checklistType, new Date(), rawTz)
    }
    const bookId = bookIdFor(meta.workspaceId, meta.checklistType)
    promises.push(
      (async () => {
        const book = await ensureRecurringBookWindow(meta.workspaceId, meta.checklistType, bookActor(actor))
        const page = await ensurePageForKey(book.id, meta.workspaceId, meta.checklistType, pageKey, bookActor(actor))
        const anchorVersionId = page.layoutVersionId || book.activeLayoutVersionId
        if (!anchorVersionId) return
        const layout = await getLayoutVersion(book.id, anchorVersionId)
        if (!layout) return
        const nextNodes = {
          ...layout.nodes,
          [id]: { checklistId: id, position },
        }
        await applyEditToPage(bookId, meta.workspaceId, pageKey, { nodes: nextNodes, wires: layout.wires }, bookActor(actor))
      })(),
    )
  }

  await Promise.all(promises)
}

// ---------------------------------------------------------------------------
// Internal helper — spread a recurring checklist across every layout-version
// group in its parent-scoped window.
// ---------------------------------------------------------------------------
//
// Prior behavior: a new DAILY / WEEKLY item ran `findEmptyCanvasSlot` once
// against TODAY'S layout version and let `applyEditToPage` fan the new node
// forward through pages sharing that version. Any page whose layoutVersionId
// had DIVERGED (a user dragged a sibling item on a specific date) sat outside
// the fanout — the render path fell back to the definition's own `position`,
// which was computed against today's neighbours and could overlap whatever
// existed on the divergent page.
//
// New behavior: iterate every existing page in the item's recurringWindow,
// group them by their current layoutVersionId, and for each unique group
// anchor `applyEditToPage` at the group's earliest page with a slot computed
// against THAT version's own nodes. Idempotent — a group whose version
// already contains the item at a finite position is skipped, so retries and
// re-imports don't churn versions.
//
// Lazy-page model preserved: only pages that already exist are visited. When
// the window covers a range where no pages exist yet (fresh workspace, or
// parent scheduled far in the future) we ensure the earliest in-window page
// so the item has at least one anchor to live on; pages created later
// lazily inherit the nearest prior version via `resolveInheritedVersion`,
// which by then includes our item.

type RecurringSpreadWindow = { startDate: string; endDate: string }

const parseIsoDay = (iso: string): Date | null => {
  const day = iso.slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null
  const [y, m, d] = day.split('-').map(Number)
  return new Date(y, m - 1, d)
}

const spreadChecklistAcrossRecurringPages = async (params: {
  bookId: string
  workspaceId: string
  type: 'DAILY' | 'WEEKLY'
  checklistId: string
  recurringWindow: RecurringSpreadWindow
  preferredPosition: { x: number; y: number } | null
  actor: ChecklistWriterActor
}): Promise<void> => {
  const { bookId, workspaceId, type, checklistId, recurringWindow, preferredPosition, actor } = params
  const windowStartDate = parseIsoDay(recurringWindow.startDate)
  const windowEndDate = parseIsoDay(recurringWindow.endDate)
  if (!windowStartDate || !windowEndDate) return
  if (windowEndDate.getTime() < windowStartDate.getTime()) return

  const now = new Date()
  // Anchor from today when the window has already started; otherwise from the
  // window's own start so a future-dated parent still gets a real anchor page.
  const anchorDate = now.getTime() >= windowStartDate.getTime() ? now : windowStartDate
  const anchorPageKey = pageKeyFor(type, anchorDate)
  const windowEndPageKey = pageKeyFor(type, windowEndDate)
  // Both DAILY (YYYY-MM-DD) and WEEKLY (YYYY-Www) sort lexicographically in
  // chronological order, so string compare is safe.
  if (anchorPageKey > windowEndPageKey) return

  const allPages = await listPages(bookId).catch(() => [])
  const inWindowPages = allPages.filter(
    (page) => page.pageKey >= anchorPageKey && page.pageKey <= windowEndPageKey,
  )

  const versionToAnchor = new Map<string, string>()
  for (const page of inWindowPages) {
    if (!page.layoutVersionId) continue
    const current = versionToAnchor.get(page.layoutVersionId)
    if (current === undefined || page.pageKey < current) {
      versionToAnchor.set(page.layoutVersionId, page.pageKey)
    }
  }

  // No existing pages inside the window yet — bootstrap the earliest one so
  // this item has a home. Future pages created lazily will inherit the
  // resulting version via resolveInheritedVersion.
  if (versionToAnchor.size === 0) {
    const anchorPage = await ensurePageForKey(bookId, workspaceId, type, anchorPageKey, bookActor(actor))
    if (!anchorPage.layoutVersionId) return
    versionToAnchor.set(anchorPage.layoutVersionId, anchorPage.pageKey)
  }

  for (const [versionId, anchorKey] of versionToAnchor) {
    const layout = await getLayoutVersion(bookId, versionId)
    if (!layout) continue

    // Idempotency: skip if the item is already anchored somewhere finite in
    // this version. Guards against retries, replays, and the case where a
    // prior spread already covered this version group.
    const existingNode = layout.nodes[checklistId]
    if (existingNode
      && existingNode.position
      && Number.isFinite(existingNode.position.x)
      && Number.isFinite(existingNode.position.y)) {
      continue
    }

    const existingBoxes = Object.values(layout.nodes)
      .filter((node) => node.checklistId !== checklistId)
      .map((node) => toSlotBox({ position: node.position, nodeKind: 'CHECKLIST' }))
    const resolvedPosition = findEmptyCanvasSlot({
      existing: existingBoxes,
      newNode: { width: NODE_WIDTH, height: NODE_HEIGHT },
      preferred: preferredPosition,
    })
    const nextNodes = {
      ...layout.nodes,
      [checklistId]: { checklistId, position: resolvedPosition },
    }
    await applyEditToPage(
      bookId,
      workspaceId,
      anchorKey,
      { nodes: nextNodes, wires: layout.wires },
      bookActor(actor),
    ).catch((err) => {
      // Per-version-group best-effort: log at warn level so failures show up
      // in the console, but don't abort the loop — one bad version group
      // shouldn't prevent the item from landing on the others.
      console.warn('[spreadChecklistAcrossRecurringPages] group write skipped', {
        versionId, anchorKey, error: (err as Error)?.message,
      })
    })
  }
}

// ---------------------------------------------------------------------------
// Public API — create
// ---------------------------------------------------------------------------

export type CreateChecklistInput = {
  id?: string
  workspaceId: string
  parentContainerId: string
  title: string
  board?: TimelineBoardKey
  checklistType?: ChecklistType
  priority?: number
  weightage?: number
  initialPosition?: { x: number; y: number }
  extra?: Partial<ChecklistDefinition>
}

export const createChecklistNative = async (
  input: CreateChecklistInput,
  actor: ChecklistWriterActor,
): Promise<string> => {
  const id = input.id ?? doc(collection(firestoreDb, 'checklists')).id
  const type = input.checklistType ?? 'INDIVIDUAL'
  const nowActorName = actor.displayName ?? null

  let resolvedBoard: TimelineBoardKey = (input.board ?? (input.extra as { board?: TimelineBoardKey } | undefined)?.board) as TimelineBoardKey
  type ParentElementData = { type?: 'GOAL' | 'TASK' | 'SUBTASK'; startDate?: string | null; endDate?: string | null; board?: TimelineBoardKey }
  let parentElementData: ParentElementData | null = null

  if (input.parentContainerId && !isSyntheticParentId(input.parentContainerId)) {
    const parentSnap = await getDoc(doc(firestoreDb, 'elements', input.parentContainerId))
    if (parentSnap.exists()) {
      parentElementData = parentSnap.data() as unknown as ParentElementData
      if (!resolvedBoard) {
        resolvedBoard = parentElementData?.board ?? 'DASHBOARD'
      }
    }
  }
  if (!resolvedBoard) {
    resolvedBoard = 'DASHBOARD'
  }

  // Parent-scoped recurring: DAILY / WEEKLY must live inside a real
  // Task/Goal/Subtask with both dates set. Rejects legacy synthetic
  // `recurring:{ws}:{DAILY|WEEKLY}` parents outright.
  let recurringWindow: ChecklistDefinition['recurringWindow'] | undefined
  if (type === 'DAILY' || type === 'WEEKLY') {
    if (isLegacyRecurringContainerId(input.parentContainerId)) {
      throw new Error(
        'Top-level recurring canvases are retired — add a recurring checklist inside a Task, Goal, or Subtask instead.',
      )
    }
    const eligibility = canParentHostRecurring(parentElementData)
    if (!eligibility.ok) throw new Error(eligibility.reason)
    recurringWindow = {
      parentElementId: input.parentContainerId,
      startDate: (parentElementData!.startDate as string).slice(0, 10),
      endDate: (parentElementData!.endDate as string).slice(0, 10),
      syncedAt: serverTimestamp(),
    }
  }

  const definition: Record<string, unknown> = {
    id,
    workspaceId: input.workspaceId,
    parentContainerId: input.parentContainerId,
    checklistType: type,
    title: input.title,
    priority: input.priority ?? 3,
    weightage: input.weightage ?? 1,
    prerequisites: [],
    blockedByExternal: null,
    isArchived: false,
    nodeKind: 'CHECKLIST',
    board: resolvedBoard,
    ...input.extra,
    ...(recurringWindow ? { recurringWindow } : {}),
    createdAt: serverTimestamp(),
    createdById: actor.uid,
    createdByName: nowActorName,
    updatedAt: serverTimestamp(),
    lastUpdatedBy: actor.uid,
    lastUpdatedByName: nowActorName,
    recordVersion: 1,
  }

  // 1. Definition doc (with counter stepping in transaction).
  await runChecklistCreateTransaction(firestoreDb, {
    workspaceId: input.workspaceId,
    board: resolvedBoard,
    parentContainerId: input.parentContainerId,
    checklistRef: doc(firestoreDb, checklistPath(id)),
    checklistData: definition,
  })
  invalidateChecklistDefinitionListCache(input.workspaceId, type)

  // 2. Book bootstrap + append node + seed completion (INDIVIDUAL only).
  const book = await ensureRecurringBookWindow(input.workspaceId, type, bookActor(actor))
  const { fetchWorkspaceRawTimezone } = await import('./checklistStatusWriter')
  const rawTz = await fetchWorkspaceRawTimezone(input.workspaceId)
  const pageKey = pageKeyFor(type, new Date(), rawTz)
  const page = await ensurePageForKey(book.id, input.workspaceId, type, pageKey, bookActor(actor))
  if (type === 'INDIVIDUAL') {
    await setChecklistCompletionOnPage(
      book.id,
      pageKey,
      id,
      { isCompleted: false, boardStatus: null, workflowStatus: 'CREATED' },
      bookActor(actor),
    )
  }
  // 3. Layout placement.
  // - INDIVIDUAL: single 'single' page, keep the existing today-only write.
  // - DAILY / WEEKLY: spread across every layout-version group in the item's
  //   recurringWindow so divergent pages get a locally-resolved empty slot
  //   instead of inheriting today's position and colliding with whatever
  //   already occupies that spot on future pages.
  if (type === 'DAILY' || type === 'WEEKLY') {
    if (recurringWindow) {
      await spreadChecklistAcrossRecurringPages({
        bookId: book.id,
        workspaceId: input.workspaceId,
        type,
        checklistId: id,
        recurringWindow: {
          startDate: recurringWindow.startDate,
          endDate: recurringWindow.endDate,
        },
        preferredPosition: input.initialPosition ?? null,
        actor,
      })
    }
    // If recurringWindow is somehow missing here (canParentHostRecurring
    // should have thrown above), skip the layout write rather than fanning
    // out an unbounded item — the completion doc still exists.
  } else {
    const anchorVersionId = page.layoutVersionId || book.activeLayoutVersionId
    if (anchorVersionId) {
      const layout = await getLayoutVersion(book.id, anchorVersionId)
      if (layout) {
        // Same hard-and-fast rule as addChecklistToBookLayout: run the
        // caller's `initialPosition` through the shared placement resolver
        // so every external creator (BoardExplorer, QuickCreate, AI Line)
        // spreads across empty slots instead of piling at (0, 0).
        const existingBoxes = Object.values(layout.nodes).map((node) => toSlotBox({
          position: node.position,
          nodeKind: 'CHECKLIST',
        }))
        const resolvedPosition = findEmptyCanvasSlot({
          existing: existingBoxes,
          newNode: { width: NODE_WIDTH, height: NODE_HEIGHT },
          preferred: input.initialPosition ?? null,
        })
        const nextNodes = {
          ...layout.nodes,
          [id]: {
            checklistId: id,
            position: resolvedPosition,
          },
        }
        await applyEditToPage(book.id, input.workspaceId, pageKey, { nodes: nextNodes, wires: layout.wires }, bookActor(actor))
      }
    }
  }

  return id
}

// ---------------------------------------------------------------------------
// Public API — delete
// ---------------------------------------------------------------------------

export const deleteChecklistNative = async (
  id: string,
  actor: ChecklistWriterActor,
): Promise<void> => {
  const meta = await readChecklist(id)

  if (meta) {
    const bookId = bookIdFor(meta.workspaceId, meta.checklistType)
    const isDailyOrWeekly = meta.checklistType === 'DAILY' || meta.checklistType === 'WEEKLY'
    let pageKey: string
    if (isDailyOrWeekly) {
      const { fetchWorkspaceRawTimezone } = await import('./checklistStatusWriter')
      const { workspaceTodayKey } = await import('./repeat/keys')
      const rawTz = await fetchWorkspaceRawTimezone(meta.workspaceId)
      pageKey = workspaceTodayKey(meta.checklistType as 'DAILY' | 'WEEKLY', rawTz)
    } else {
      const { fetchWorkspaceRawTimezone } = await import('./checklistStatusWriter')
      const rawTz = await fetchWorkspaceRawTimezone(meta.workspaceId)
      pageKey = pageKeyFor(meta.checklistType, new Date(), rawTz)
    }
    await removeChecklistFromPage(bookId, pageKey, id, bookActor(actor))
      .catch(swallowNotFound('checklistWriter.page-removal'))
    // Prune node from the current layout version so the canvas hides it.
    const bookSnap = await getDoc(doc(firestoreDb, `checklist_books/${bookId}`))
    const activeLayoutVersionId = bookSnap.exists()
      ? ((bookSnap.data() as { activeLayoutVersionId?: string }).activeLayoutVersionId ?? null)
      : null
    if (activeLayoutVersionId) {
      const layout = await getLayoutVersion(bookId, activeLayoutVersionId)
      if (layout && layout.nodes[id]) {
        const nextNodes = { ...layout.nodes }
        delete nextNodes[id]
        const nextWires = layout.wires.filter((w) => w.from !== id && w.to !== id)
        await applyEditToPage(bookId, meta.workspaceId, pageKey, { nodes: nextNodes, wires: nextWires }, bookActor(actor))
          .catch(swallowNotFound('checklistWriter.layout-prune'))
      }
    }
  }

  if (meta) {
    if (meta.board === 'BIN') {
      await deleteChecklistForever(meta.workspaceId, [id])
        .catch(swallowNotFound('checklistWriter.definition-delete'))
    } else {
      await binChecklist(meta.workspaceId, [id])
        .catch(swallowNotFound('checklistWriter.definition-bin'))
      await deleteChecklistForever(meta.workspaceId, [id])
        .catch(swallowNotFound('checklistWriter.definition-delete'))
    }
    invalidateChecklistDefinitionListCache(meta.workspaceId, meta.checklistType)
  }
}

// ---------------------------------------------------------------------------
// Public API — bootstrap on the board (post-legacy-write)
// ---------------------------------------------------------------------------

export type BootstrapChecklistBoardItem = {
  id: string
  checklistType: ChecklistType
  position?: { x: number; y: number }
  /** Parent-scoped range mirrored from the owning Task/Goal/Subtask. Passed
   *  through so DAILY/WEEKLY items can be spread across every layout-version
   *  group in the window (see spreadChecklistAcrossRecurringPages).
   *  Omitted for INDIVIDUAL items — INDIVIDUAL books have a single page and
   *  the bulk today-write below covers them fully. */
  recurringWindow?: { startDate: string; endDate: string }
}

/**
 * Attach one-or-more freshly-created checklist definitions to their
 * `checklist_books/{ws}_{type}` boards so they render on the Individual /
 * Daily / Weekly surfaces. Idempotent — every step (book, page, layout
 * append) merges instead of overwriting, so replaying is safe.
 *
 * Why this exists: `syncChecklistBoardItems` writes the definition doc via a
 * legacy batch. The server-side `mirrorDefinitionCompletionToPage` trigger
 * only patches completions on an EXISTING page, so a fresh workspace (or a
 * type whose book has never been used) never gets a page and the items stay
 * invisible on the board. This helper does the client-side bootstrap that
 * `createChecklistNative` already does for user-created items, but for the
 * bulk AI-drafted case where the definition is written separately.
 *
 * Groups items by type to fold every same-type add into ONE layout version
 * bump; without grouping we'd mint N versions for N same-type items and
 * only the last one would win the page repoint anyway.
 *
 * The helper NEVER throws — a board-visibility failure must not block the
 * container/checklist definition writes that already succeeded. Errors are
 * logged; the caller stays green.
 */
export const bootstrapChecklistsOnBoard = async (
  workspaceId: string,
  items: BootstrapChecklistBoardItem[],
  actor: ChecklistWriterActor,
): Promise<void> => {
  if (items.length === 0) return
  const byType = new Map<ChecklistType, BootstrapChecklistBoardItem[]>()
  for (const item of items) {
    const list = byType.get(item.checklistType) ?? []
    list.push(item)
    byType.set(item.checklistType, list)
  }
  await Promise.all(Array.from(byType.entries()).map(async ([type, list]) => {
    try {
      const book = await ensureRecurringBookWindow(workspaceId, type, bookActor(actor))
      const { fetchWorkspaceRawTimezone } = await import('./checklistStatusWriter')
      const rawTz = await fetchWorkspaceRawTimezone(workspaceId)
      const pageKey = pageKeyFor(type, new Date(), rawTz)
      const page = await ensurePageForKey(book.id, workspaceId, type, pageKey, bookActor(actor))
      // Seed initial completions in parallel for INDIVIDUAL items only — set(merge:true)
      // upstream, safe to replay if the same id already exists.
      if (type === 'INDIVIDUAL') {
        await Promise.all(list.map((item) =>
          setChecklistCompletionOnPage(
            book.id,
            pageKey,
            item.id,
            { isCompleted: false, boardStatus: null, workflowStatus: 'CREATED' },
            bookActor(actor),
          ).catch((err) => console.warn('[bootstrapChecklistsOnBoard] completion seed skipped', {
            type, checklistId: item.id, error: (err as Error)?.message,
          })),
        ))
      }
      const anchorVersionId = page.layoutVersionId || book.activeLayoutVersionId
      if (!anchorVersionId) return
      const layout = await getLayoutVersion(book.id, anchorVersionId)
      if (!layout) return
      // Collision-aware bulk placement: evolve `nextNodes` as we go so each
      // subsequent item sees the previous one and lands in a genuinely empty
      // slot, not on top of it. Also remember each item's today-slot so the
      // per-window spread below can use it as a preferred hint.
      let evolvingNodes: typeof layout.nodes = { ...layout.nodes }
      const todaySlotByItemId = new Map<string, { x: number; y: number }>()
      list.forEach((item) => {
        // Idempotency: if the layout already has this item at a finite
        // position (retry / replay), keep it — don't move nodes around.
        const existingNode = evolvingNodes[item.id]
        if (existingNode
          && existingNode.position
          && Number.isFinite(existingNode.position.x)
          && Number.isFinite(existingNode.position.y)) {
          todaySlotByItemId.set(item.id, existingNode.position)
          return
        }
        const existingBoxes = Object.values(evolvingNodes)
          .filter((node) => node.checklistId !== item.id)
          .map((node) => toSlotBox({ position: node.position, nodeKind: 'CHECKLIST' }))
        const position = findEmptyCanvasSlot({
          existing: existingBoxes,
          newNode: { width: NODE_WIDTH, height: NODE_HEIGHT },
          preferred: item.position ?? null,
        })
        evolvingNodes = {
          ...evolvingNodes,
          [item.id]: { checklistId: item.id, position },
        }
        todaySlotByItemId.set(item.id, position)
      })
      await applyEditToPage(
        book.id,
        workspaceId,
        pageKey,
        { nodes: evolvingNodes, wires: layout.wires },
        bookActor(actor),
      )

      // For DAILY / WEEKLY, also spread each item across every layout-version
      // group in its recurringWindow so divergent future pages get a locally-
      // resolved slot instead of inheriting the definition's today position
      // and colliding with sibling items on that page. The today's-page group
      // is skipped as a no-op (item is already in that version). Runs
      // sequentially per item so an early failure just short-circuits that
      // one item — the completions and today-write above already succeeded.
      if (type === 'DAILY' || type === 'WEEKLY') {
        for (const item of list) {
          if (!item.recurringWindow) continue
          await spreadChecklistAcrossRecurringPages({
            bookId: book.id,
            workspaceId,
            type,
            checklistId: item.id,
            recurringWindow: item.recurringWindow,
            preferredPosition: todaySlotByItemId.get(item.id) ?? item.position ?? null,
            actor,
          }).catch((err) => console.warn('[bootstrapChecklistsOnBoard] spread skipped', {
            type, checklistId: item.id, error: (err as Error)?.message,
          }))
        }
      }
    } catch (err) {
      // Never let a board-bootstrap failure cascade. The definitions are
      // already persisted; the board can be rebuilt on next visit via the
      // ensureBook / mirror trigger flow.
      // Top-level per-type swallow is intentional — the function's contract
      // is "never throw; definitions are already persisted, board can rebuild
      // on next visit." Bumped to warn so failures are visible in the
      // console rather than only under debug logging.
      console.warn('[bootstrapChecklistsOnBoard] failed', {
        type, workspaceId, error: (err as Error)?.message,
      })
    }
  }))
}

// ---------------------------------------------------------------------------
// Board-level canvas spread — mirrors the "Spread out overlapping nodes"
// toolbar button in ChecklistDrawingBoard, but at the service layer so a
// caller (currently: post-AI-generation) can trigger it in the background and
// persist the result once. Scoped to the INDIVIDUAL book's today page (the
// only surface AI-generated checklists land on today).
//
// Idempotent — walks nodes top-left-first, keeps every node in place when its
// current position doesn't collide with what's already been accepted, and
// only re-runs `findEmptyCanvasSlot` for the ones that do. If nothing moved
// (the common case — `bootstrapChecklistsOnBoard` already avoids overlap
// per-item), no write happens.
//
// Best-effort by contract: never throws. The AI Move-to-Manage handoff must
// not be blocked by a layout cleanup pass.
// ---------------------------------------------------------------------------

export const spreadIndividualBoardCanvas = async (
  workspaceId: string,
  actor: ChecklistWriterActor,
): Promise<void> => {
  try {
    const book = await ensureRecurringBookWindow(workspaceId, 'INDIVIDUAL', bookActor(actor))
    const pageKey = pageKeyFor('INDIVIDUAL', new Date())
    const page = await ensurePageForKey(book.id, workspaceId, 'INDIVIDUAL', pageKey, bookActor(actor))
    const versionId = page.layoutVersionId || book.activeLayoutVersionId
    if (!versionId) return
    const layout = await getLayoutVersion(book.id, versionId)
    if (!layout) return

    const ordered = Object.entries(layout.nodes).sort(([, a], [, b]) => {
      const ay = a.position?.y ?? 0
      const by = b.position?.y ?? 0
      if (ay !== by) return ay - by
      const ax = a.position?.x ?? 0
      const bx = b.position?.x ?? 0
      return ax - bx
    })

    const connectedIds = new Set<string>()
    if (layout.wires) {
      Object.values(layout.wires).forEach((wire) => {
        if (wire && typeof wire === 'object') {
          if (typeof wire.from === 'string') connectedIds.add(wire.from)
          if (typeof wire.to === 'string') connectedIds.add(wire.to)
        }
      })
    }

    const accepted: Array<{ position: { x: number; y: number }; width: number; height: number }> = []
    const nextNodes: typeof layout.nodes = {}
    let movedCount = 0

    // Seed accepted with connected nodes' current positions as fixed obstacles
    for (const [id, node] of Object.entries(layout.nodes)) {
      if (connectedIds.has(id)) {
        const current = node.position ?? { x: 0, y: 0 }
        accepted.push({ position: current, width: NODE_WIDTH, height: NODE_HEIGHT })
        nextNodes[id] = node
      }
    }

    // Only relocate free (unconnected) nodes
    const freeOrdered = ordered.filter(([id]) => !connectedIds.has(id))
    for (const [id, node] of freeOrdered) {
      const current = node.position ?? { x: 0, y: 0 }
      const resolved = findEmptyCanvasSlot({
        existing: accepted,
        newNode: { width: NODE_WIDTH, height: NODE_HEIGHT },
        preferred: current,
      })
      if (resolved.x !== current.x || resolved.y !== current.y) movedCount += 1
      accepted.push({ position: resolved, width: NODE_WIDTH, height: NODE_HEIGHT })
      nextNodes[id] = { ...node, position: resolved }
    }

    if (movedCount === 0) return
    await applyEditToPage(
      book.id,
      workspaceId,
      pageKey,
      { nodes: nextNodes, wires: layout.wires },
      bookActor(actor),
    )
  } catch (err) {
    console.warn('[spreadIndividualBoardCanvas] skipped', {
      workspaceId, error: (err as Error)?.message,
    })
  }
}

// ---------------------------------------------------------------------------
// Convenience — arrayUnion / deleteField exports so replacement sites don't
// need extra Firestore imports.
// ---------------------------------------------------------------------------

export { arrayUnion, deleteField, increment, writeBatch }
